import { useTranslation } from 'react-i18next';

import { type AuditLogFilters, addAuditFilters, applyAuditFilterChange } from '@/domain/auditLog';

import FilterTextField from '../../FilterTextField';
import FilterMenu from '../FilterMenu';
import type { AuditFilterBindings } from '../filterBindings';
import { actorTypeSections } from '../filterOptions';

import DateTimeFilter from './DateTimeFilter';
import FilterGroup from './FilterGroup';
import ImpersonationSelect from './ImpersonationSelect';
import PanelField from './PanelField';

interface WhenWhoFiltersProps {
    bindings: AuditFilterBindings;
}

export default function WhenWhoFilters({ bindings }: WhenWhoFiltersProps) {
    const { filters, onChange, onType } = bindings;
    const { t } = useTranslation();
    const oneActorType = filters.actorType?.length === 1;

    function bound(key: 'from' | 'to') {
        return (value: Date | undefined, step: 'pick' | 'type' | 'commit') => {
            const next: AuditLogFilters = { ...filters };
            if (value === undefined) delete next[key];
            else next[key] = value;
            if (step === 'pick') onChange(next);
            else onType(next, step);
        };
    }

    return (
        <>
            <FilterGroup legend={t('auditLog.filters.group.when')}>
                <DateTimeFilter
                    label={t('auditLog.filters.from')}
                    value={filters.from}
                    onChange={bound('from')}
                />
                <DateTimeFilter
                    label={t('auditLog.filters.to')}
                    value={filters.to}
                    onChange={bound('to')}
                    hint={t('auditLog.filters.toHint')}
                />
                <FilterTextField
                    field="entryId"
                    label={t('auditLog.filters.entryId')}
                    filters={filters}
                    onType={onType}
                />
            </FilterGroup>
            <FilterGroup legend={t('auditLog.filters.group.who')}>
                <FilterTextField
                    field="actorEmail"
                    label={t('auditLog.filters.person')}
                    placeholder={t('auditLog.filters.personPlaceholder')}
                    filters={filters}
                    onType={onType}
                />
                <PanelField label={t('auditLog.filters.actorType')}>
                    {(labelId) => (
                        <FilterMenu
                            label={t('auditLog.filters.actorType')}
                            labelledBy={labelId}
                            options={actorTypeSections(t)}
                            selected={filters.actorType}
                            onChange={(actorType) =>
                                onChange(
                                    applyAuditFilterChange(filters, addAuditFilters({ actorType }))
                                )
                            }
                        />
                    )}
                </PanelField>
                <FilterTextField
                    field="actorUserId"
                    label={t('auditLog.filters.actorUserId')}
                    filters={filters}
                    onType={onType}
                    // Users and API users share ids, so an id means nothing without its type.
                    disabled={!oneActorType}
                    hint={oneActorType ? undefined : t('auditLog.filters.actorUserIdHint')}
                />
                <PanelField label={t('auditLog.filters.impersonation')}>
                    {(labelId) => (
                        <ImpersonationSelect
                            filters={filters}
                            onChange={onChange}
                            labelledBy={labelId}
                        />
                    )}
                </PanelField>
                <FilterTextField
                    field="impersonatorEmail"
                    label={t('auditLog.filters.impersonatorEmail')}
                    placeholder={t('auditLog.filters.containsPlaceholder')}
                    filters={filters}
                    onType={onType}
                />
            </FilterGroup>
        </>
    );
}
